import React, { useEffect, useState, useMemo, useCallback, memo } from "react";
import "./DualListBox.scss";

/** Match StaffId / UserId / StudyId across formats (GUID braces, casing). */
export const normListId = (v) =>
  String(v ?? "")
    .replace(/[{}]/g, "")
    .trim()
    .toLowerCase();

/**
 * @param {object} props
 * @param {() => Promise<unknown[]>} [props.fetchSelectedItems] — used when `initialSelectedIds` is omitted
 * @param {unknown[]} [props.initialSelectedIds] — when set (including []), used directly instead of fetch (reliable sync)
 */
const DualListBox = memo(({
  fetchSelectedItems,
  allItems = [],
  onChange,
  initialSelectedIds,
}) => {
  const [availableItems, setAvailableItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [searchAvailable, setSearchAvailable] = useState("");
  const [searchSelected, setSearchSelected] = useState("");

  const initialIdsSig = useMemo(() => {
    if (initialSelectedIds === undefined) return "__fetch__";
    return JSON.stringify(
      [...initialSelectedIds].map((id) => normListId(id)).sort(),
    );
  }, [initialSelectedIds]);

  const allItemsSig = useMemo(
    () =>
      allItems
        .map((i) => normListId(i.value ?? i.label))
        .filter(Boolean)
        .sort()
        .join(","),
    [allItems],
  );

  // Initialize lists when staff list or selected ids change
  useEffect(() => {
    let cancelled = false;
    const initLists = async () => {
      try {
        let selectedIds = [];
        if (initialSelectedIds !== undefined) {
          selectedIds = initialSelectedIds;
        } else if (fetchSelectedItems) {
          selectedIds = await fetchSelectedItems();
        }
        if (cancelled) return;
        if (!Array.isArray(selectedIds)) selectedIds = [];
        if (selectedIds.length && Array.isArray(selectedIds[0]))
          selectedIds = selectedIds[0];

        const selectedSet = new Set(
          selectedIds.map((id) => normListId(id)).filter(Boolean),
        );

        const selected = [];
        const available = [];
        for (let i = 0; i < allItems.length; i++) {
          const item = allItems[i];
          const key = normListId(item.value ?? item.label);
          if (key && selectedSet.has(key)) {
            selected.push(item);
          } else {
            available.push(item);
          }
        }

        setSelectedItems(selected);
        setAvailableItems(available);
        // Don't call onChange during init - avoids unnecessary parent updates & re-renders
      } catch (err) {
        if (!cancelled) {
          setSelectedItems([]);
          setAvailableItems(allItems);
        }
      }
    };

    initLists();
    return () => {
      cancelled = true;
    };
  }, [fetchSelectedItems, initialIdsSig, allItemsSig]);

  const updateLists = useCallback((newSelected, newAvailable) => {
    setSelectedItems(newSelected);
    setAvailableItems(newAvailable);
    onChange?.(newSelected, newAvailable);
  }, [onChange]);

  const handleSelect = useCallback((item) => {
    setSelectedItems((prev) => {
      const newSelected = [...prev, item];
      setAvailableItems((prevAvail) => {
        const newAvailable = prevAvail.filter((i) => i.value !== item.value);
        onChange?.(newSelected, newAvailable);
        return newAvailable;
      });
      return newSelected;
    });
  }, [onChange]);

  const handleRemove = useCallback((item) => {
    setSelectedItems((prev) => {
      const newSelected = prev.filter((i) => i.value !== item.value);
      setAvailableItems((prevAvail) => {
        const newAvailable = [...prevAvail, item];
        onChange?.(newSelected, newAvailable);
        return newAvailable;
      });
      return newSelected;
    });
  }, [onChange]);

  const moveAllToSelected = useCallback(() => {
    setSelectedItems((prev) => {
      const newSelected = [...prev, ...availableItems];
      setAvailableItems([]);
      onChange?.(newSelected, []);
      return newSelected;
    });
  }, [availableItems, onChange]);

  const removeAll = useCallback(() => {
    setAvailableItems((prev) => {
      const newAvailable = [...prev, ...selectedItems];
      setSelectedItems([]);
      onChange?.([], newAvailable);
      return newAvailable;
    });
  }, [selectedItems, onChange]);

  const searchLower = (searchAvailable || "").toLowerCase();
  const searchSelLower = (searchSelected || "").toLowerCase();
  const filteredAvailable = useMemo(
    () =>
      searchLower
        ? availableItems.filter((item) =>
            (item.label ?? "").toLowerCase().includes(searchLower)
          )
        : availableItems,
    [availableItems, searchLower]
  );
  const filteredSelected = useMemo(
    () =>
      searchSelLower
        ? selectedItems.filter((item) =>
            (item.label ?? "").toLowerCase().includes(searchSelLower)
          )
        : selectedItems,
    [selectedItems, searchSelLower]
  );

  return (
    <div className="dual-list-box-container">
      {/* Available list */}
      <div className="list-box">
        <div className="list-header">
          <strong>Available</strong>
          <span>{availableItems.length} items</span>
        </div>
        <input
          className="form-control search-box"
          placeholder="Search..."
          value={searchAvailable}
          onChange={(e) => setSearchAvailable(e.target.value)}
        />
        <div className="list-scroll">
          {filteredAvailable.map((item) => (
            <div
              key={item.value}
              className="list-item"
              onClick={() => handleSelect(item)}
            >
              {item.label}
            </div>
          ))}
        </div>
        <button
          className="btn btn-outline-primary action-btn"
          onClick={moveAllToSelected}
        >
          Move All →
        </button>
      </div>

      {/* Selected list */}
      <div className="list-box">
        <div className="list-header">
          <strong>Selected</strong>
          <span>{selectedItems.length} items</span>
        </div>
        <input
          className="form-control search-box"
          placeholder="Search..."
          value={searchSelected}
          onChange={(e) => setSearchSelected(e.target.value)}
        />
        <div className="list-scroll">
          {filteredSelected.map((item) => (
            <div
              key={item.value}
              className="list-item"
              onClick={() => handleRemove(item)}
            >
              {item.label}
            </div>
          ))}
        </div>
        <button
          className="btn btn-outline-danger action-btn"
          onClick={removeAll}
        >
          ← Remove All
        </button>
      </div>
    </div>
  );
});

DualListBox.displayName = "DualListBox";

export default DualListBox;
